import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import { api } from '../services/api';
import {
  Shield, Users, CheckCircle, XCircle, Loader2,
  UserPlus, Briefcase, Clock, Eye, MessageSquare, Send,
  Hexagon, TrendingUp, Wallet, Car, Palette, Wine, Gem, ShoppingBag, Watch,
  Inbox, ShieldAlert, Share2, ExternalLink,
} from 'lucide-react';
import TicketThreadDrawer from '../components/TicketThreadDrawer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { FEATURES } from '../config/features';

export default function PartnerDashboard() {
  const { user } = useAuth();
  const { formatCurrency } = useCurrency();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('inbox');
  const [dashboard, setDashboard] = useState(null);
  const [clients, setClients] = useState([]);
  const [inbox, setInbox] = useState({ tickets: [], shares: [], counts: { tickets_open: 0, tickets_total: 0, shares_pending: 0, shares_total: 0 } });
  const [loading, setLoading] = useState(true);
  const [threadTicket, setThreadTicket] = useState(null); // { id, client_name }
  const [actionLoading, setActionLoading] = useState(null);
  const [showRegisterClient, setShowRegisterClient] = useState(false);
  const [clientForm, setClientForm] = useState({ email: '', full_name: '', password: '' });
  const [clientOverview, setClientOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewTab, setOverviewTab] = useState('assets');

  const iconMap = { car: Car, art: Palette, wine: Wine, jewelry: Gem, handbag: ShoppingBag, watch: Watch };
  const categoryColor = (c) => ({
    car: 'text-amber-400', art: 'text-purple-400', wine: 'text-red-400',
    jewelry: 'text-pink-400', handbag: 'text-rose-400', watch: 'text-blue-400'
  }[c] || 'text-gold');

  const openClientOverview = async (clientId) => {
    setOverviewLoading(true);
    setClientOverview({ loading: true });
    try {
      const res = await api.getClientOverview(clientId);
      setClientOverview(res.data);
      setOverviewTab('assets');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Unable to load client overview');
      setClientOverview(null);
    } finally {
      setOverviewLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role !== 'partner') { navigate('/dashboard'); return; }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dashRes, clientsRes, inboxRes] = await Promise.all([
        api.getPartnerDashboard(),
        api.getPartnerClients(),
        api.getPartnerInbox().catch(() => ({ data: null })),
      ]);
      setDashboard(dashRes.data);
      setClients(clientsRes.data || []);
      if (inboxRes.data) setInbox(inboxRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleVerify = async (id, verified) => {
    setActionLoading(id);
    try {
      await api.partnerVerify(id, verified);
      toast.success(verified ? 'Verification approved.' : 'Verification rejected.');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setActionLoading(null); }
  };

  const handleRegisterClient = async () => {
    if (!clientForm.email || !clientForm.full_name || !clientForm.password) {
      toast.error('All fields are required'); return;
    }
    try {
      await api.partnerRegisterClient(clientForm);
      toast.success(`Client ${clientForm.full_name} registered successfully.`);
      setShowRegisterClient(false);
      setClientForm({ email: '', full_name: '', password: '' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Registration failed'); }
  };

  if (loading && !dashboard) {
    return <DashboardLayout><div className="flex items-center justify-center h-96"><Loader2 size={32} className="text-gold animate-spin" /></div></DashboardLayout>;
  }

  const pending = dashboard?.pending_verifications || [];
  const completed = dashboard?.completed_verifications || [];

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="partner-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-gold uppercase tracking-[0.2em] text-xs mb-2">Partner Portal</p>
            <h1 className="font-serif text-3xl text-white">Wealth Manager Dashboard</h1>
            <p className="text-muted-foreground text-sm mt-1">{user?.company || 'Partner Account'}</p>
          </div>
          <Button onClick={() => setShowRegisterClient(true)} className="bg-gold text-black hover:bg-gold/90 text-xs" data-testid="register-client-btn">
            <UserPlus size={14} className="mr-1" /> Register Client
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="luxury-card p-4"><p className="text-xs text-muted-foreground">Inbox - Open Tickets</p><p className="text-2xl text-gold font-semibold">{inbox.counts.tickets_open}</p></div>
          <div className="luxury-card p-4"><p className="text-xs text-muted-foreground">Inbox - Pending Shares</p><p className="text-2xl text-gold font-semibold">{inbox.counts.shares_pending}</p></div>
          <div className="luxury-card p-4"><p className="text-xs text-muted-foreground">Pending Verifications</p><p className="text-2xl text-white font-semibold">{pending.length}</p></div>
          <div className="luxury-card p-4"><p className="text-xs text-muted-foreground">Clients</p><p className="text-2xl text-white font-semibold">{clients.length}</p></div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-[#0A0A0A] border border-[#262626] p-1 rounded w-fit flex-wrap">
          {[
            { key: 'inbox', label: 'Inbox', icon: Inbox, badge: inbox.counts.tickets_open + inbox.counts.shares_pending },
            { key: 'verifications', label: 'Verifications', icon: Shield, badge: pending.length },
            { key: 'clients', label: 'My Clients', icon: Users },
          ].map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key)} data-testid={`partner-tab-${t.key}`}
              className={`flex items-center gap-2 px-4 py-2 text-xs rounded transition-colors ${activeTab === t.key ? 'bg-gold text-black' : 'text-muted-foreground hover:text-white'}`}>
              <t.icon size={14} /> {t.label}
              {t.badge > 0 && <span className="ml-1 text-[10px] bg-red-500 text-white px-1.5 rounded-full">{t.badge}</span>}
            </button>
          ))}
        </div>

        {/* Inbox Tab */}
        {activeTab === 'inbox' && (
          <div className="space-y-6" data-testid="inbox-view">
            <div>
              <h3 className="text-gold text-sm font-medium mb-3 flex items-center gap-2">
                <ShieldAlert size={14} /> Action tickets routed to you ({inbox.tickets.length})
              </h3>
              {inbox.tickets.length === 0 ? (
                <p className="text-xs text-muted-foreground pl-4">No tickets yet. When your clients tag you on an action, it will appear here.</p>
              ) : (
                <div className="space-y-3">
                  {inbox.tickets.map(t => (
                    <div key={t.id} className="luxury-card p-4" data-testid={`inbox-ticket-${t.id}`}>
                      <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-[10px] px-2 py-0.5 rounded uppercase tracking-wider ${
                              t.issue === 'uninsured' ? 'bg-red-500/15 text-red-400' :
                              t.issue === 'underinsured' ? 'bg-amber-500/15 text-amber-400' :
                              t.issue === 'expiring' ? 'bg-gold/15 text-gold' :
                              'bg-zinc-500/15 text-zinc-300'
                            }`}>{t.issue}</span>
                            <span className={`text-[10px] px-2 py-0.5 rounded ${t.status === 'open' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-zinc-500/15 text-zinc-400'}`}>{t.status}</span>
                            <p className="text-white text-sm font-medium truncate">{t.title}</p>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            <span className="text-zinc-300">{t.client_name}</span>
                            {t.client_email && <span> - {t.client_email}</span>}
                            {t.asset_name && <span> - asset: {t.asset_name}</span>}
                          </p>
                          {t.detail && <p className="text-xs text-zinc-400 mt-2 whitespace-pre-wrap line-clamp-4">{t.detail}</p>}
                          <p className="text-[10px] text-muted-foreground mt-2">Opened {new Date(t.created_at).toLocaleString()}</p>
                        </div>
                        {t.client_email && (
                          <button
                            type="button"
                            onClick={() => setThreadTicket({ id: t.id, client_name: t.client_name })}
                            className="inline-flex items-center gap-1 text-xs text-gold hover:text-amber-300 px-2 py-1 rounded border border-gold/30 hover:border-gold/60"
                            data-testid={`inbox-ticket-reply-${t.id}`}
                          >
                            <MessageSquare size={12} /> Open thread
                            {(t.notes?.length || 0) > 0 && (
                              <span className="ml-1 text-[10px] bg-gold/15 text-gold px-1.5 rounded-full">{t.notes.length}</span>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <h3 className="text-gold text-sm font-medium mb-3 flex items-center gap-2">
                <Share2 size={14} /> Encrypted PII shares ({inbox.shares.length})
              </h3>
              {inbox.shares.length === 0 ? (
                <p className="text-xs text-muted-foreground pl-4">No PII shares yet. When your clients route a secure field set to you, it will appear here with a one-time view link.</p>
              ) : (
                <div className="space-y-3">
                  {inbox.shares.map(s => {
                    const used = !!s.accessed_at;
                    const expired = new Date(s.expires_at) < new Date();
                    const url = `${window.location.origin}${s.share_url}`;
                    const status = s.revoked ? 'revoked' : used ? 'viewed' : expired ? 'expired' : 'pending';
                    const statusCls = {
                      pending: 'bg-emerald-500/15 text-emerald-400',
                      viewed:  'bg-zinc-500/15 text-zinc-300',
                      expired: 'bg-amber-500/15 text-amber-400',
                      revoked: 'bg-red-500/15 text-red-400',
                    }[status];
                    return (
                      <div key={s.id} className="luxury-card p-4" data-testid={`inbox-share-${s.id}`}>
                        <div className="flex items-start justify-between gap-4 flex-wrap">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`text-[10px] px-2 py-0.5 rounded uppercase tracking-wider ${statusCls}`}>{status}</span>
                              <p className="text-white text-sm font-medium truncate">{s.purpose || 'Secure field share'}</p>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                              <span className="text-zinc-300">{s.client_name}</span>
                              {s.client_email && <span> - {s.client_email}</span>}
                            </p>
                            <p className="text-xs text-zinc-400 mt-2">Fields: {s.fields.join(', ')}</p>
                            <p className="text-[10px] text-muted-foreground mt-1">Expires {new Date(s.expires_at).toLocaleString()}</p>
                          </div>
                          {status === 'pending' && (
                            <a
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-gold hover:text-amber-300"
                              data-testid={`inbox-share-open-${s.id}`}
                            >
                              <ExternalLink size={12} /> Open one-time view
                            </a>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Verifications Tab */}
        {activeTab === 'verifications' && (
          <div className="space-y-6" data-testid="verifications-view">
            {/* Pending */}
            <div>
              <h3 className="text-amber-400 text-sm font-medium mb-3 flex items-center gap-2"><Clock size={14} /> Pending Verification ({pending.length})</h3>
              {pending.length === 0 ? (
                <p className="text-xs text-muted-foreground pl-4">No pending verifications.</p>
              ) : (
                <div className="space-y-3">
                  {pending.map(v => (
                    <div key={v.id} className="luxury-card p-5" data-testid={`verification-${v.id}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white font-medium">{v.asset_name || v.asset_id?.slice(0, 12)}</p>
                          <p className="text-xs text-muted-foreground">Policy: {v.policy_number || 'Not provided'}</p>
                          <p className="text-xs text-muted-foreground">Requested: {new Date(v.created_at).toLocaleDateString()}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button onClick={() => handleVerify(v.id, true)} disabled={!!actionLoading} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs" data-testid={`verify-approve-${v.id}`}>
                            {actionLoading === v.id ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle size={12} className="mr-1" />} Verify
                          </Button>
                          <Button onClick={() => handleVerify(v.id, false)} disabled={!!actionLoading} size="sm" variant="outline" className="border-red-500/30 text-red-400 text-xs" data-testid={`verify-reject-${v.id}`}>
                            <XCircle size={12} className="mr-1" /> Reject
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Completed */}
            <div>
              <h3 className="text-emerald-400 text-sm font-medium mb-3 flex items-center gap-2"><CheckCircle size={14} /> Completed ({completed.length})</h3>
              {completed.length === 0 ? (
                <p className="text-xs text-muted-foreground pl-4">No completed verifications yet.</p>
              ) : (
                <div className="space-y-2">
                  {completed.map(v => (
                    <div key={v.id} className="luxury-card p-4 flex items-center justify-between">
                      <div><p className="text-white text-sm">{v.asset_name || v.asset_id?.slice(0, 12)}</p><p className="text-xs text-muted-foreground">{v.notes || ''}</p></div>
                      <span className={`text-xs px-2 py-0.5 rounded ${v.status === 'verified' ? 'text-emerald-400 bg-emerald-400/10' : 'text-red-400 bg-red-400/10'}`}>{v.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Clients Tab */}
        {activeTab === 'clients' && (
          <div className="space-y-4" data-testid="clients-view">
            {clients.length === 0 ? (
              <div className="luxury-card p-8 text-center">
                <Users size={32} className="text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-muted-foreground text-sm mb-3">No clients registered yet.</p>
                <Button onClick={() => setShowRegisterClient(true)} className="bg-gold text-black text-xs"><UserPlus size={12} className="mr-1" /> Register First Client</Button>
              </div>
            ) : (
              clients.map(client => (
                <button key={client.id} onClick={() => openClientOverview(client.id)} className="luxury-card p-4 flex items-center gap-4 w-full text-left hover:border-gold/40 transition-colors" data-testid={`client-${client.id}`}>
                  <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center"><Users size={16} className="text-gold" /></div>
                  <div className="flex-1">
                    <p className="text-white text-sm font-medium">{client.full_name}</p>
                    <p className="text-xs text-muted-foreground">{client.email}</p>
                  </div>
                  <div className="text-right hidden sm:block">
                    <p className="text-xs text-muted-foreground">{client.asset_count || 0} assets{FEATURES.TOKENISATION_ENABLED && ` · ${client.token_count || 0} tokens`}</p>
                    <p className="text-[10px] text-muted-foreground capitalize">{client.subscription_tier} tier{FEATURES.TOKENISATION_ENABLED && ` · DT ${client.digital_twin_access || 'none'}`}</p>
                  </div>
                  <Eye size={14} className="text-gold ml-2" />
                </button>
              ))
            )}
          </div>
        )}

        {/* Client Overview Modal */}
        <Dialog open={!!clientOverview} onOpenChange={(open) => !open && setClientOverview(null)}>
          <DialogContent className="bg-[#0A0A0A] border-[#262626] max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="client-overview-modal">
            <DialogHeader>
              <DialogTitle className="font-serif text-white">
                {clientOverview?.client?.full_name || 'Client Overview'}
              </DialogTitle>
            </DialogHeader>
            {overviewLoading || !clientOverview || clientOverview.loading ? (
              <div className="flex items-center justify-center py-16"><Loader2 size={32} className="text-gold animate-spin" /></div>
            ) : (
              <div className="space-y-5">
                {/* Client summary */}
                <div className={`grid grid-cols-2 ${FEATURES.TOKENISATION_ENABLED ? 'md:grid-cols-4' : 'md:grid-cols-2'} gap-3`}>
                  <div className="p-3 bg-[#0F0F0F] rounded">
                    <p className="text-[10px] text-muted-foreground uppercase">Email</p>
                    <p className="text-white text-xs truncate">{clientOverview.client.email}</p>
                  </div>
                  <div className="p-3 bg-[#0F0F0F] rounded">
                    <p className="text-[10px] text-muted-foreground uppercase">Tier</p>
                    <p className="text-white text-xs capitalize">{clientOverview.client.subscription_tier || 'free'}</p>
                  </div>
                  {FEATURES.TOKENISATION_ENABLED && (
                    <>
                      <div className="p-3 bg-[#0F0F0F] rounded">
                        <p className="text-[10px] text-muted-foreground uppercase">Digital Twin</p>
                        <p className="text-white text-xs capitalize">{clientOverview.client.digital_twin_access || 'none'}</p>
                      </div>
                      <div className="p-3 bg-[#0F0F0F] rounded">
                        <p className="text-[10px] text-muted-foreground uppercase">Wallet</p>
                        <p className="text-gold text-xs font-semibold">{formatCurrency(clientOverview.client.wallet_balance || 0)}</p>
                      </div>
                    </>
                  )}
                </div>

                {/* Totals */}
                <div className={`grid ${FEATURES.TOKENISATION_ENABLED ? 'grid-cols-3' : 'grid-cols-1'} gap-3`}>
                  <div className="p-4 bg-gradient-to-br from-gold/10 to-transparent border border-gold/20 rounded">
                    <div className="flex items-center gap-2 mb-1"><Briefcase size={12} className="text-gold" /><p className="text-[10px] text-gold uppercase">Assets</p></div>
                    <p className="text-white text-xl font-semibold">{clientOverview.totals.asset_count}</p>
                    <p className="text-xs text-muted-foreground">{formatCurrency(clientOverview.totals.asset_value)}</p>
                  </div>
                  {FEATURES.TOKENISATION_ENABLED && (
                    <>
                      <div className="p-4 bg-gradient-to-br from-[#627EEA]/10 to-transparent border border-[#627EEA]/20 rounded">
                        <div className="flex items-center gap-2 mb-1"><Hexagon size={12} className="text-[#627EEA]" /><p className="text-[10px] text-[#627EEA] uppercase">Digital Twins</p></div>
                        <p className="text-white text-xl font-semibold">{clientOverview.totals.token_count}</p>
                        <p className="text-xs text-muted-foreground">tokenized</p>
                      </div>
                      <div className="p-4 bg-gradient-to-br from-emerald-500/10 to-transparent border border-emerald-500/20 rounded">
                        <div className="flex items-center gap-2 mb-1"><TrendingUp size={12} className="text-emerald-400" /><p className="text-[10px] text-emerald-400 uppercase">Holdings</p></div>
                        <p className="text-white text-xl font-semibold">{clientOverview.totals.holdings_count}</p>
                        <p className="text-xs text-muted-foreground">{formatCurrency(clientOverview.totals.holdings_value)}</p>
                      </div>
                    </>
                  )}
                </div>

                {/* Sub-tabs */}
                <div className="flex gap-1 bg-[#0A0A0A] border border-[#262626] p-1 rounded w-fit">
                  {[
                    { key: 'assets', label: 'Assets', count: clientOverview.totals.asset_count },
                    ...(FEATURES.TOKENISATION_ENABLED ? [
                      { key: 'tokens', label: 'Digital Twins', count: clientOverview.totals.token_count },
                      { key: 'holdings', label: 'Holdings', count: clientOverview.totals.holdings_count },
                    ] : []),
                    { key: 'orders', label: 'Transactions', count: clientOverview.recent_orders?.length || 0 },
                  ].map(t => (
                    <button key={t.key} onClick={() => setOverviewTab(t.key)} data-testid={`overview-tab-${t.key}`}
                      className={`flex items-center gap-2 px-3 py-1.5 text-xs rounded transition-colors ${overviewTab === t.key ? 'bg-gold text-black' : 'text-muted-foreground hover:text-white'}`}>
                      {t.label} <span className="text-[10px] opacity-70">{t.count}</span>
                    </button>
                  ))}
                </div>

                {/* Assets */}
                {overviewTab === 'assets' && (
                  clientOverview.assets.length === 0 ? (
                    <p className="text-xs text-muted-foreground italic">This client has not added any assets yet.</p>
                  ) : (
                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                      {clientOverview.assets.map(a => {
                        const Icon = iconMap[a.category] || Briefcase;
                        return (
                          <div key={a.id} className="flex items-center gap-3 p-3 bg-[#0F0F0F] rounded" data-testid={`client-asset-${a.id}`}>
                            <div className="w-9 h-9 rounded bg-[#0A0A0A] flex items-center justify-center flex-shrink-0">
                              {a.image_url ? <img src={a.image_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${a.image_url}` : a.image_url} alt="" className="w-full h-full object-cover rounded" /> : <Icon size={14} className={categoryColor(a.category)} />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-white truncate">{a.name}</p>
                              <p className="text-[10px] text-muted-foreground capitalize">{a.category} · {a.condition || 'condition n/a'}</p>
                            </div>
                            <p className="text-gold text-sm">{formatCurrency(a.estimated_value || 0)}</p>
                          </div>
                        );
                      })}
                    </div>
                  )
                )}

                {/* Tokens */}
                {overviewTab === 'tokens' && (
                  clientOverview.tokens.length === 0 ? (
                    <p className="text-xs text-muted-foreground italic">No digital twins minted yet.</p>
                  ) : (
                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                      {clientOverview.tokens.map(t => {
                        const Icon = iconMap[t.category] || Hexagon;
                        return (
                          <div key={t.id} className="flex items-center gap-3 p-3 bg-[#0F0F0F] rounded" data-testid={`client-token-${t.id}`}>
                            <div className="w-9 h-9 rounded bg-[#0A0A0A] flex items-center justify-center flex-shrink-0">
                              {t.image_url ? <img src={t.image_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${t.image_url}` : t.image_url} alt="" className="w-full h-full object-cover rounded" /> : <Icon size={14} className={categoryColor(t.category)} />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-white truncate">{t.asset_name}</p>
                              <p className="text-[10px] text-muted-foreground">{t.fractions_available}/{t.total_fractions} fractions avail. · {formatCurrency(t.price_per_fraction)}/frac</p>
                            </div>
                            <span className={`text-[10px] px-2 py-0.5 rounded ${t.status === 'listed' ? 'text-emerald-400 bg-emerald-400/10' : t.status === 'sold_out' ? 'text-gold bg-gold/10' : 'text-[#627EEA] bg-[#627EEA]/10'}`}>{t.status}</span>
                          </div>
                        );
                      })}
                    </div>
                  )
                )}

                {/* Holdings */}
                {overviewTab === 'holdings' && (
                  clientOverview.holdings.length === 0 ? (
                    <p className="text-xs text-muted-foreground italic">Client has not purchased any fractions.</p>
                  ) : (
                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                      {clientOverview.holdings.map(h => {
                        const Icon = iconMap[h.category] || Hexagon;
                        return (
                          <div key={h.token_id} className="flex items-center gap-3 p-3 bg-[#0F0F0F] rounded" data-testid={`client-holding-${h.token_id}`}>
                            <Icon size={16} className={categoryColor(h.category)} />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-white truncate">{h.asset_name}</p>
                              <p className="text-[10px] text-muted-foreground">{h.fractions_owned} / {h.total_fractions} fractions</p>
                            </div>
                            <p className="text-gold text-sm">{formatCurrency(h.current_value)}</p>
                          </div>
                        );
                      })}
                    </div>
                  )
                )}

                {/* Orders */}
                {overviewTab === 'orders' && (
                  clientOverview.recent_orders.length === 0 ? (
                    <p className="text-xs text-muted-foreground italic">No transactions yet.</p>
                  ) : (
                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                      {clientOverview.recent_orders.map(o => (
                        <div key={o.id} className="flex items-center gap-3 p-3 bg-[#0F0F0F] rounded">
                          <span className={`px-2 py-0.5 text-[10px] rounded font-medium ${o.side === 'buy' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>{(o.side || 'buy').toUpperCase()}</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-white truncate">{o.asset_name}</p>
                            <p className="text-[10px] text-muted-foreground">{o.fractions} fractions @ {formatCurrency(o.price_per_fraction)}</p>
                          </div>
                          <p className="text-gold text-sm">{formatCurrency(o.total_value)}</p>
                        </div>
                      ))}
                    </div>
                  )
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Register Client Modal */}
        <Dialog open={showRegisterClient} onOpenChange={setShowRegisterClient}>
          <DialogContent className="bg-[#0A0A0A] border-[#262626] max-w-md" data-testid="register-client-modal">
            <DialogHeader><DialogTitle className="font-serif text-white">Register New Client</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <p className="text-xs text-muted-foreground">Create an Assagart account on behalf of your client. They'll be linked to your portfolio.</p>
              <div><label className="text-xs text-muted-foreground uppercase tracking-wider">Client Full Name</label><Input value={clientForm.full_name} onChange={e => setClientForm({...clientForm, full_name: e.target.value})} placeholder="John Ashworth" className="luxury-input mt-1" /></div>
              <div><label className="text-xs text-muted-foreground uppercase tracking-wider">Client Email</label><Input value={clientForm.email} onChange={e => setClientForm({...clientForm, email: e.target.value})} placeholder="john@email.com" className="luxury-input mt-1" /></div>
              <div><label className="text-xs text-muted-foreground uppercase tracking-wider">Temporary Password</label><Input value={clientForm.password} onChange={e => setClientForm({...clientForm, password: e.target.value})} placeholder="Min 8 characters" className="luxury-input mt-1" /></div>
              <Button onClick={handleRegisterClient} className="w-full bg-gold text-black hover:bg-gold/90" data-testid="confirm-register-client">Register Client</Button>
              <p className="text-[10px] text-muted-foreground text-center">Client will be assigned to your portfolio. Share credentials securely.</p>
            </div>
          </DialogContent>
        </Dialog>

        <TicketThreadDrawer
          ticketId={threadTicket?.id}
          clientName={threadTicket?.client_name}
          open={!!threadTicket}
          onClose={() => { setThreadTicket(null); fetchData(); }}
          viewerRole="partner"
        />
      </div>
    </DashboardLayout>
  );
}
